/**
 * useOIDCSession - Unified OIDC Session Management Hook
 *
 * Consolidates token refresh, activity tracking, and timeout dialog
 * into a single hook with proper cross-tab coordination.
 *
 * Features:
 * - Web Locks API for leader election (AbortController-based, no timers)
 * - Activity tracking with localStorage as source of truth
 * - Token refresh with exponential backoff
 * - Terminal error detection (no retry on invalid_grant, etc.)
 * - Dialog state using absolute timestamps for accurate countdown
 * - Cross-tab synchronization via BroadcastChannel
 */

import { useState, useEffect, useCallback, useRef, useContext } from 'react';
import {
  useAuth as useOIDCAuth,
  AuthContext as OIDCAuthContext,
} from 'react-oidc-context';
import { jwtDecode } from 'jwt-decode';
import { getAuthFromLocalStorage } from '../utils/authUtils';
import { createBackoffController } from '../utils/backoff';
import { setTokenRefreshHandlers } from '../utils/bbAxios';
import {
  setToken as setSocketToken,
  initSocketManager,
  disconnectAll as disconnectAllSockets,
} from '../utils/socketManager';
import { subscribe, broadcast, closeAllChannels } from '../utils/sessionChannelManager';
import {
  getTabId,
  resetTabId,
  readTimestamp,
  writeTimestamp,
  clearStorageKeys,
  clearStorageByPattern,
} from '../utils/storageUtils';
import { getTokenExpiration } from '../utils/tokenUtils';
import { createLeaderElection } from '../utils/leaderElection';
import { createActivityManager } from '../utils/activityManager';
import { sessionLog } from '../utils/sessionLog';
import {
  MIN_REFRESH_INTERVAL_MS,
  DIALOG_CHECK_INTERVAL_MS,
  MAX_RETRIES,
  STORAGE_KEYS,
  CHANNEL_NAMES,
  MESSAGE_TYPES,
  LOCK_NAMES,
  OIDC_REFRESH_TIMEOUT_MS,
  FOLLOWER_WAIT_TIMEOUT_MS,
  REFRESH_LEEWAY_MS,
  WARNING_THRESHOLD_MS,
  STORAGE_KEY_DEBOUNCE_MS,
  STORAGE_KEY_DEBOUNCE_REFRESHING_MS,
  EXPIRED_SAFETY_NET_MS,
  isTerminalError,
} from '../sessionConfig';

// ============================================================================
// MODULE-LEVEL SINGLETONS
// ============================================================================

// Leader election controller
let leaderController = null;

// Activity manager instance
let activityManager = null;

/**
 * Get or create leader controller singleton
 */
const getLeaderController = () => {
  if (!leaderController) {
    leaderController = createLeaderElection({
      lockName: LOCK_NAMES.OIDC,
      logPrefix: '[OIDCSession]',
    });
  }
  return leaderController;
};

/**
 * Get or create activity manager singleton
 */
const getActivityManager = () => {
  if (!activityManager) {
    activityManager = createActivityManager({
      activityKey: STORAGE_KEYS.ACTIVITY,
      refreshKey: STORAGE_KEYS.REFRESH,
      channelName: CHANNEL_NAMES.OIDC_ACTIVITY,
      logPrefix: '[OIDCSession]',
    });
  }
  return activityManager;
};

/**
 * Clear OIDC-related localStorage keys
 */
const clearOIDCStorage = () => {
  try {
    clearStorageKeys(Object.values(STORAGE_KEYS));
  } catch (error) {
    console.error('[OIDCSession] Failed to clear storage:', error);
  }
};

/**
 * Reset all module state (called on logout)
 */
const resetModuleState = () => {
  resetTabId();
  getActivityManager().reset();
  getLeaderController().reset();
};

// ============================================================================
// MAIN HOOK
// ============================================================================

/**
 * Unified OIDC Session Hook
 *
 * @param {Object} options
 * @param {string} options.loginRoute - Route to redirect on logout
 * @param {Function} options.onSessionExpired - Called when session expires
 * @returns {Object} Session state and controls
 */
export const useOIDCSession = ({ loginRoute = '/bbweb/login', onSessionExpired }) => {
  // Always call useOIDCAuth unconditionally to satisfy React's rules of hooks.
  // The context check is only used to determine if the result is usable.
  const oidcContext = useContext(OIDCAuthContext);
  const oidcAuthResult = useOIDCAuth();
  const oidcAuth = oidcContext ? oidcAuthResult : null;

  const leader = getLeaderController();
  const activity = getActivityManager();

  // Stabilize onSessionExpired via ref to prevent dependency cascade.
  // This callback is often passed as an inline function, and countdown state
  // changes every second cause re-renders that create new function identities,
  // which would cascade through performRefresh → scheduleRefresh → the main effect.
  const onSessionExpiredRef = useRef(onSessionExpired);
  useEffect(() => {
    onSessionExpiredRef.current = onSessionExpired;
  });

  // React state for UI
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [dialogExpirationTimestamp, setDialogExpirationTimestamp] = useState(null);
  const [countdown, setCountdown] = useState(0);

  // Refs for stable callbacks
  const oidcAuthRef = useRef(oidcAuth);
  const isDialogOpenRef = useRef(false);
  const backoffRef = useRef(createBackoffController({ maxRetries: MAX_RETRIES }));
  const lastRefreshAttemptRef = useRef(0);
  const refreshTimeoutRef = useRef(null);
  const dialogCheckIntervalRef = useRef(null);
  const countdownIntervalRef = useRef(null);
  const isRefreshingRef = useRef(false);
  // Shared promise for in-flight refresh — allows concurrent callers to await the same
  // refresh instead of racing or returning null. Prevents double-invocation from
  // safety-net/scheduler overlap from triggering parallel SDK refresh exchanges.
  const refreshPromiseRef = useRef(null);
  // Track current token's IAT for activity comparison (server timestamp as source of truth)
  const currentTokenIatMsRef = useRef(null);
  // Track known expiration from broadcast messages (faster than localStorage sync)
  const knownExpirationRef = useRef(null);
  // Debounce timer for oidc.user: storage key removal detection
  const storageKeyRemovedTimeoutRef = useRef(null);

  // Keep refs updated
  useEffect(() => {
    oidcAuthRef.current = oidcAuth;
  }, [oidcAuth]);

  useEffect(() => {
    isDialogOpenRef.current = isDialogOpen;
  }, [isDialogOpen]);

  // ============================================================================
  // LOGOUT HANDLER
  // ============================================================================

  const handleLogout = useCallback(
    (reason = 'user', { skipBroadcast = false } = {}) => {
      if (handleLogout._inProgress) return;
      handleLogout._inProgress = true;
      sessionLog(`[OIDCSession] 🚪 Logging out (reason: ${reason})`);

      try {
        disconnectAllSockets();
      } catch (e) {
        console.warn('[OIDCSession] Socket cleanup error during logout:', e);
      }
      clearOIDCStorage();

      // Broadcast BEFORE closing channels so we use the existing channel instance.
      // skipBroadcast=true when this logout was triggered by another tab's broadcast,
      // preventing a broadcast storm across many tabs.
      if (!skipBroadcast) {
        broadcast(CHANNEL_NAMES.OIDC_SESSION, {
          type: MESSAGE_TYPES.LOGOUT_REQUESTED,
          reason,
        });
      }

      closeAllChannels();
      resetModuleState();

      if (refreshTimeoutRef.current) clearTimeout(refreshTimeoutRef.current);
      if (dialogCheckIntervalRef.current) clearInterval(dialogCheckIntervalRef.current);
      if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);

      // Selectively clear OIDC library keys (oidc-client-ts stores with 'oidc.' prefix)
      // instead of blanket localStorage.clear() which would nuke other apps' data.
      clearStorageByPattern('oidc.');
      sessionStorage.clear();

      // Include current path as redirect so login sends the user back.
      // Strip any existing 'redirect' param to prevent nested redirect chains
      // (e.g., when authSync and handleLogout race, or from stale query params).
      // Skip redirect param when already on the login page to prevent
      // /login?redirect=%2Flogin loops after re-login.
      const url = new URL(window.location.href);
      url.searchParams.delete('redirect');
      const isAlreadyOnLogin = url.pathname.endsWith('/login');
      const separator = loginRoute.includes('?') ? '&' : '?';
      if (isAlreadyOnLogin) {
        window.location.href = loginRoute;
      } else {
        const returnUrl = url.pathname + url.search;
        window.location.href = `${loginRoute}${separator}redirect=${encodeURIComponent(returnUrl)}`;
      }
    },
    [loginRoute]
  );

  // ============================================================================
  // TOKEN REFRESH
  // ============================================================================

  const performRefresh = useCallback(async () => {
    // ── Deduplication ─────────────────────────────────────────────────
    // If a refresh is already in flight, share its promise so concurrent
    // callers receive the same result instead of racing or returning null.
    if (refreshPromiseRef.current) {
      return refreshPromiseRef.current;
    }

    const currentOidcAuth = oidcAuthRef.current;

    // Pre-flight checks
    if (!currentOidcAuth?.user) {
      sessionLog('[OIDCSession] ⏸️ Skipping refresh - no user');
      return null;
    }
    if (isDialogOpenRef.current) {
      sessionLog('[OIDCSession] ⏸️ Skipping refresh - dialog is open');
      return null;
    }
    if (!leader.isLeader()) {
      return null; // Normal for followers, no log needed
    }
    if (!navigator.onLine) {
      sessionLog('[OIDCSession] ⏸️ Skipping refresh - offline');
      return null;
    }

    // Activity check using token's IAT as source of truth when available
    const currentIatMs = currentTokenIatMsRef.current;
    const isActive = currentIatMs
      ? activity.isUserActiveForToken(currentIatMs)
      : activity.isUserActive();
    if (!isActive) {
      sessionLog('[OIDCSession] ⏸️ Skipping refresh - user not active', {
        lastActivity: activity.getLastActivity()
          ? Math.round((Date.now() - activity.getLastActivity()) / 1000) + 's ago'
          : 'never',
        tokenIatMs: currentIatMs
          ? Math.round((Date.now() - currentIatMs) / 1000) + 's ago'
          : 'unknown',
      });
      return null;
    }

    const now = Date.now();
    if (now - lastRefreshAttemptRef.current < MIN_REFRESH_INTERVAL_MS) return null;
    lastRefreshAttemptRef.current = now;

    // ── Tracked refresh promise ──────────────────────────────────────
    const refreshWork = (async () => {
      isRefreshingRef.current = true;

      try {
        const user = await Promise.race([
          currentOidcAuth.signinSilent(),
          new Promise((_, reject) =>
            setTimeout(
              () => reject(new Error('OIDC refresh timeout')),
              OIDC_REFRESH_TIMEOUT_MS
            )
          ),
        ]);

        const newToken = user?.access_token;
        if (!newToken) {
          throw new Error('OIDC refresh succeeded but no token available');
        }

        const decoded = jwtDecode(newToken);
        const newExpiration = decoded?.exp ? decoded.exp * 1000 : null;
        const newIatMs = decoded?.iat ? decoded.iat * 1000 : null;

        sessionLog(
          `[OIDCSession] ✅ Refreshed, expires in ${Math.round((newExpiration - Date.now()) / 1000)}s`
        );

        // Update IAT tracking ref
        if (newIatMs) {
          currentTokenIatMsRef.current = newIatMs;
        }

        backoffRef.current.reset();
        knownExpirationRef.current = newExpiration;

        // Update refresh timestamp only — activity must come from real user interaction
        activity.updateRefreshTimestamp(newIatMs || Date.now());

        broadcast(CHANNEL_NAMES.OIDC_SESSION, {
          type: MESSAGE_TYPES.TOKEN_REFRESHED,
          expiration: newExpiration,
          tabId: getTabId(),
          tokenIatMs: newIatMs,
        });

        // Update socket manager auth for future reconnections
        setSocketToken(newToken);

        return { token: newToken, expiration: newExpiration };
      } catch (error) {
        sessionLog(`[OIDCSession] ❌ Refresh failed: ${error.message}`);

        if (isTerminalError(error)) {
          handleLogout('terminal_error');
          // Re-throw so callers (e.g., bbAxios) can detect the terminal error
          // and trip the circuit breaker to prevent further refresh attempts
          throw error;
        }

        if (backoffRef.current.canRetry()) {
          const delay = backoffRef.current.nextDelay();
          refreshTimeoutRef.current = setTimeout(performRefresh, delay);
        } else {
          backoffRef.current.reset();
          onSessionExpiredRef.current?.();
        }

        return null;
      } finally {
        isRefreshingRef.current = false;
      }
    })();

    refreshPromiseRef.current = refreshWork;
    try {
      return await refreshWork;
    } finally {
      refreshPromiseRef.current = null;
    }
  }, [handleLogout, activity, leader]);

  const scheduleRefresh = useCallback(async () => {
    if (!leader.isLeader()) return;
    if (isDialogOpenRef.current) {
      sessionLog('[OIDCSession] ⏰ Skipping schedule - dialog is open');
      return;
    }
    if (!oidcAuthRef.current?.user) {
      sessionLog('[OIDCSession] ⏰ Skipping schedule - no user');
      return;
    }

    try {
      const authData = getAuthFromLocalStorage();
      const token = authData?.access_token;
      if (!token) {
        sessionLog('[OIDCSession] ⏰ Skipping schedule - no token in storage');
        return;
      }

      const decoded = jwtDecode(token);
      if (!decoded?.exp) {
        console.warn('[OIDCSession] ⏰ Token has no exp claim');
        return;
      }

      const expiresAtMs = decoded.exp * 1000;
      const iatMs = decoded.iat ? decoded.iat * 1000 : null;
      const now = Date.now();
      const leeway = WARNING_THRESHOLD_MS + REFRESH_LEEWAY_MS;
      const delayMs = expiresAtMs - now - leeway;

      // Initialize socket manager and set initial token
      initSocketManager();
      setSocketToken(token);

      // Update tracking ref for activity comparison (mirrors Auth0 hook)
      if (iatMs) {
        currentTokenIatMsRef.current = iatMs;
      }

      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
      }

      if (delayMs <= 0) {
        if (now - lastRefreshAttemptRef.current >= MIN_REFRESH_INTERVAL_MS) {
          sessionLog('[OIDCSession] ⏰ Refresh due now');
          const result = await performRefresh();
          if (result) scheduleRefresh();
        } else {
          const waitTime =
            MIN_REFRESH_INTERVAL_MS - (now - lastRefreshAttemptRef.current) + 100;
          refreshTimeoutRef.current = setTimeout(() => scheduleRefresh(), waitTime);
        }
      } else {
        sessionLog(
          `[OIDCSession] ⏰ Refresh scheduled in ${Math.round(delayMs / 1000)}s`
        );
        refreshTimeoutRef.current = setTimeout(async () => {
          refreshTimeoutRef.current = null; // Timer has fired, clear ref
          const result = await performRefresh();
          if (result) scheduleRefresh();
        }, delayMs);
      }
    } catch (error) {
      console.error('[OIDCSession] Error scheduling refresh:', error);
    }
  }, [performRefresh, leader]);

  // ============================================================================
  // DIALOG MANAGEMENT
  // ============================================================================

  const showDialog = useCallback(
    (expiration) => {
      if (isDialogOpenRef.current) return;

      sessionLog(
        `[OIDCSession] ⚠️ Dialog opening, ${Math.round((expiration - Date.now()) / 1000)}s until timeout`
      );

      // Update ref synchronously BEFORE setIsDialogOpen to prevent race:
      // BroadcastChannel DIALOG_SHOWN from another tab can arrive before
      // the useEffect that syncs the ref, causing showDialog to run twice.
      isDialogOpenRef.current = true;
      setIsDialogOpen(true);
      setDialogExpirationTimestamp(expiration);
      writeTimestamp(STORAGE_KEYS.DIALOG_OPEN, Date.now());

      // Stop silent renew while dialog is open
      oidcAuthRef.current?.stopSilentRenew?.();

      broadcast(CHANNEL_NAMES.OIDC_SESSION, {
        type: MESSAGE_TYPES.DIALOG_SHOWN,
        expiration,
        tabId: getTabId(),
      });

      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
      }

      const updateCountdown = () => {
        const remaining = Math.max(0, Math.ceil((expiration - Date.now()) / 1000));
        setCountdown(remaining);

        if (remaining <= 0) {
          clearInterval(countdownIntervalRef.current);
          countdownIntervalRef.current = null;
          handleLogout('timeout');
        }
      };

      updateCountdown();
      countdownIntervalRef.current = setInterval(updateCountdown, 1000);
    },
    [handleLogout]
  );

  const dismissDialog = useCallback(() => {
    sessionLog('[OIDCSession] 🔕 Dismissing dialog');
    isDialogOpenRef.current = false; // Sync ref immediately (mirrors showDialog pattern)
    setIsDialogOpen(false);
    setDialogExpirationTimestamp(null);
    setCountdown(0);

    try {
      localStorage.removeItem(STORAGE_KEYS.DIALOG_OPEN);
    } catch (e) {
      // Ignore
    }

    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }

    broadcast(CHANNEL_NAMES.OIDC_SESSION, {
      type: MESSAGE_TYPES.DIALOG_DISMISSED,
      tabId: getTabId(),
    });
  }, []);

  const handleContinue = useCallback(async () => {
    const currentOidcAuth = oidcAuthRef.current;
    if (!currentOidcAuth?.signinSilent) {
      dismissDialog();
      return;
    }

    try {
      const user = await currentOidcAuth.signinSilent();
      const newToken = user?.access_token;
      if (!newToken) {
        throw new Error('No token received from signinSilent');
      }

      const decoded = jwtDecode(newToken);
      const newExpiration = decoded?.exp ? decoded.exp * 1000 : null;

      sessionLog(
        `[OIDCSession] ✅ Continued, expires in ${Math.round((newExpiration - Date.now()) / 1000)}s`
      );

      activity.resetTimestamps();
      knownExpirationRef.current = newExpiration;

      broadcast(CHANNEL_NAMES.OIDC_SESSION, {
        type: MESSAGE_TYPES.TOKEN_REFRESHED,
        expiration: newExpiration,
        tabId: getTabId(),
        source: 'dialog',
      });

      dismissDialog();
      scheduleRefresh();
    } catch (error) {
      console.error('[OIDCSession] ❌ Continue refresh failed:', error);

      if (isTerminalError(error)) {
        handleLogout('terminal_error');
      } else {
        dismissDialog();
        backoffRef.current.reset();
        scheduleRefresh();
      }
    }
  }, [dismissDialog, scheduleRefresh, handleLogout, activity]);

  const checkShouldShowDialog = useCallback(() => {
    // Use broadcast expiration if available (faster than localStorage cross-tab sync)
    const expiration = knownExpirationRef.current || getTokenExpiration();
    if (!expiration) return;

    const now = Date.now();
    const remaining = expiration - now;
    const isWithinWarningWindow = remaining <= WARNING_THRESHOLD_MS && remaining > 0;

    // Activity check using token's IAT as source of truth when available
    const currentIatMs = currentTokenIatMsRef.current;
    const isActive = currentIatMs
      ? activity.isUserActiveForToken(currentIatMs)
      : activity.isUserActive();
    const lastActivity = activity.getLastActivity();
    const lastRefresh = activity.getLastRefresh();

    // Time remaining log every 10s
    sessionLog(`[OIDCSession] ${Math.round(remaining / 1000)}s | active:${isActive}`);

    // SAFETY NET 1: Token already expired (remaining <= 0)
    // This catches the case where the scheduled refresh was skipped (e.g., user was
    // inactive when it fired) and the token expired before any recovery mechanism kicked in.
    if (remaining <= 0) {
      if (isDialogOpenRef.current) {
        // Dialog is open — normally the 1s countdown interval calls handleLogout('timeout').
        // However, if the React effect cleanup ran (e.g., dependency change from leader
        // election), it clears countdownIntervalRef, leaving the dialog frozen with no
        // mechanism to enforce logout. Detect this and force logout.
        if (!countdownIntervalRef.current) {
          sessionLog('[OIDCSession] 🔧 Dialog countdown interval lost, forcing logout');
          handleLogout('timeout');
        }
        return;
      }

      // Active user: attempt recovery via refresh
      if (isActive) {
        // Safety net: if token has been expired for an unreasonable duration,
        // our activity state is likely stale (e.g., missed TOKEN_REFRESHED broadcast
        // left us with an old tokenIatMs). Force logout to prevent infinite loops
        // where the follower keeps requesting refresh from a leader that's counting down.
        if (remaining < -EXPIRED_SAFETY_NET_MS) {
          sessionLog(
            '[OIDCSession] Token deeply expired despite appearing active — forcing logout',
            { remaining: Math.round(remaining / 1000) + 's' }
          );
          handleLogout('timeout');
          return;
        }

        if (leader.isLeader() && !isRefreshingRef.current) {
          sessionLog('[OIDCSession] 🔧 Token expired, attempting refresh recovery');
          performRefresh()
            .then((result) => {
              if (result) scheduleRefresh();
            })
            .catch(() => {
              /* terminal error — handleLogout already called */
            });
        } else if (!leader.isLeader()) {
          // Ask leader to refresh on our behalf
          broadcast(CHANNEL_NAMES.OIDC_SESSION, {
            type: MESSAGE_TYPES.REFRESH_REQUESTED,
            tabId: getTabId(),
          });
        }
        return;
      }

      // Inactive user, token expired → logout
      sessionLog('[OIDCSession] Token expired while user inactive — logging out');
      handleLogout('timeout');
      return;
    }

    // SAFETY NET 2: If follower tab has an active user and token is getting low,
    // ask the leader to refresh. Without this, an active follower silently
    // watches the token expire because only the leader runs scheduleRefresh.
    if (
      remaining <= WARNING_THRESHOLD_MS + REFRESH_LEEWAY_MS &&
      remaining > 0 &&
      !leader.isLeader() &&
      isActive
    ) {
      sessionLog('[OIDCSession] 📡 Follower requesting leader refresh (user active)');
      broadcast(CHANNEL_NAMES.OIDC_SESSION, {
        type: MESSAGE_TYPES.REFRESH_REQUESTED,
        tabId: getTabId(),
      });
    }

    // SAFETY NET 3: Leader with active user and expiring token but no pending refresh.
    // This catches the dead-refresh-loop scenario: the scheduled refresh fired while
    // the user was inactive (performRefresh returned null), then user became active
    // but no new refresh was ever scheduled — leaving the token to expire silently.
    if (
      remaining <= WARNING_THRESHOLD_MS + REFRESH_LEEWAY_MS &&
      remaining > 0 &&
      leader.isLeader() &&
      isActive &&
      !refreshTimeoutRef.current &&
      !isRefreshingRef.current
    ) {
      sessionLog(
        '[OIDCSession] 🔧 Leader auto-refresh: active user, no pending refresh'
      );
      performRefresh()
        .then((result) => {
          if (result) scheduleRefresh();
        })
        .catch(() => {
          /* terminal error — handleLogout already called */
        });
      return;
    }

    if (isWithinWarningWindow && !isDialogOpenRef.current) {
      const shouldOpen = !isActive;
      sessionLog('[OIDCSession] Dialog decision', {
        shouldOpen,
        isActive,
        remaining: Math.round(remaining / 1000),
        lastActivity: lastActivity
          ? Math.round((now - lastActivity) / 1000) + 's ago'
          : 'never',
        lastRefresh: lastRefresh
          ? Math.round((now - lastRefresh) / 1000) + 's ago'
          : 'never',
        activityAfterRefresh: lastActivity > lastRefresh,
      });
      if (shouldOpen) {
        showDialog(expiration);
      }
    }
  }, [showDialog, activity, leader, performRefresh, scheduleRefresh, handleLogout]);

  // ============================================================================
  // CROSS-TAB MESSAGE HANDLING
  // ============================================================================

  const handleSessionMessage = useCallback(
    (data) => {
      switch (data.type) {
        case MESSAGE_TYPES.TOKEN_REFRESHED:
          if (data.tabId !== getTabId()) {
            sessionLog('[OIDCSession] 🔄 Token refreshed by another tab', {
              source: data.source || 'auto',
              newExpiry: data.expiration
                ? Math.round((data.expiration - Date.now()) / 1000) + 's'
                : 'unknown',
            });
            // Store broadcast expiration to avoid localStorage sync delays
            if (data.expiration) {
              knownExpirationRef.current = data.expiration;
            }
            // Sync IAT tracking from leader's broadcast
            if (data.tokenIatMs) {
              currentTokenIatMsRef.current = data.tokenIatMs;
            }
            activity.updateRefreshTimestamp(data.tokenIatMs || Date.now());
            scheduleRefresh();
            if (isDialogOpenRef.current) {
              dismissDialog();
            }
          }
          break;

        case MESSAGE_TYPES.REFRESH_REQUESTED:
          if (leader.isLeader() && data.tabId !== getTabId()) {
            sessionLog('[OIDCSession] 🔄 Follower requested refresh');
            performRefresh()
              .then((result) => {
                if (result) scheduleRefresh();
              })
              .catch(() => {
                /* terminal error — handleLogout already called */
              });
          }
          break;

        case MESSAGE_TYPES.DIALOG_SHOWN:
          if (data.tabId !== getTabId() && !isDialogOpenRef.current) {
            // Trust the broadcasting tab's decision to show the dialog.
            // Don't second-guess with local activity check — our tokenIatMs may be
            // stale if we missed a TOKEN_REFRESHED broadcast, causing us to
            // incorrectly think the user is active and ignore the dialog.
            sessionLog('[OIDCSession] 🔔 Showing dialog from cross-tab broadcast');
            showDialog(data.expiration);
          }
          break;

        case MESSAGE_TYPES.DIALOG_DISMISSED:
          if (data.tabId !== getTabId() && isDialogOpenRef.current) {
            sessionLog('[OIDCSession] 🔔 Dialog dismissed by another tab');
            dismissDialog();
          }
          break;

        case MESSAGE_TYPES.LOGOUT_REQUESTED:
          handleLogout(data.reason, { skipBroadcast: true });
          break;

        default:
          break;
      }
    },
    [
      leader,
      scheduleRefresh,
      performRefresh,
      showDialog,
      dismissDialog,
      handleLogout,
      activity,
    ]
  );

  // ============================================================================
  // SETUP AXIOS TOKEN REFRESH HANDLER
  // ============================================================================

  useEffect(() => {
    if (!oidcAuthRef.current) return;

    const oidcHandler = async () => {
      if (isDialogOpenRef.current) {
        throw new Error('Session dialog is open - user action required');
      }

      // Check if we're offline - don't attempt refresh
      if (!navigator.onLine) {
        throw new Error('Offline - cannot refresh token');
      }

      if (!leader.isLeader()) {
        // Request refresh from leader and wait for TOKEN_REFRESHED broadcast
        broadcast(CHANNEL_NAMES.OIDC_SESSION, {
          type: MESSAGE_TYPES.REFRESH_REQUESTED,
          tabId: getTabId(),
        });

        // Wait for leader to complete refresh with deterministic Promise barrier
        const waitForLeaderRefresh = () =>
          new Promise((resolve, reject) => {
            const timeout = setTimeout(() => {
              unsubscribe();
              reject(new Error('Leader refresh timeout - waited 10s'));
            }, FOLLOWER_WAIT_TIMEOUT_MS);

            const unsubscribe = subscribe(CHANNEL_NAMES.OIDC_SESSION, (data) => {
              if (data.type === MESSAGE_TYPES.TOKEN_REFRESHED) {
                clearTimeout(timeout);
                unsubscribe();
                // Read fresh token from storage
                const authData = getAuthFromLocalStorage();
                if (authData?.access_token) {
                  resolve(authData.access_token);
                } else {
                  reject(new Error('Token not found after leader refresh'));
                }
              } else if (data.type === MESSAGE_TYPES.LOGOUT_REQUESTED) {
                clearTimeout(timeout);
                unsubscribe();
                reject(new Error('Logout requested'));
              }
            });
          });

        // Try Promise barrier first, then attempt promotion if leader is dead
        try {
          return await waitForLeaderRefresh();
        } catch (barrierError) {
          // Barrier timed out — leader may have died
          sessionLog('[OIDCSession] Leader barrier timeout, attempting promotion');
          const promoted = await leader.tryClaimLeadership();
          if (promoted) {
            const result = await performRefresh();
            if (result?.token) return result.token;
          }
          throw barrierError;
        }
      }

      const result = await performRefresh();
      if (result?.token) {
        return result.token;
      }
      throw new Error('Token refresh failed');
    };

    setTokenRefreshHandlers(null, oidcHandler, 'oidc');

    return () => {
      setTokenRefreshHandlers(null, null, null);
    };
  }, [performRefresh, leader]);

  // ============================================================================
  // INITIALIZATION
  // ============================================================================

  // Use a stable boolean to avoid re-running the effect when token refreshes
  // We only care IF there's a user, not WHICH user object instance
  const hasUser = Boolean(oidcAuth?.user);
  const isLoading = oidcAuth?.isLoading;

  useEffect(() => {
    if (!oidcAuth || isLoading) return;
    if (!hasUser) return;

    sessionLog('[OIDCSession] 🚀 Initializing session', {
      tabId: getTabId(),
      isLeader: leader.isLeader(),
    });

    activity.initializeFromToken();
    activity.initializeListeners(() => isDialogOpenRef.current);

    const unsubscribeSession = subscribe(
      CHANNEL_NAMES.OIDC_SESSION,
      handleSessionMessage
    );

    leader.acquire(
      () => scheduleRefresh(),
      () => {
        if (refreshTimeoutRef.current) {
          clearTimeout(refreshTimeoutRef.current);
        }
      }
    );

    checkShouldShowDialog();
    dialogCheckIntervalRef.current = setInterval(
      checkShouldShowDialog,
      DIALOG_CHECK_INTERVAL_MS
    );

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        sessionLog('[OIDCSession] 👁️ Tab became visible', {
          isLeader: leader.isLeader(),
          hasRefreshTimeout: !!refreshTimeoutRef.current,
          isDialogOpen: isDialogOpenRef.current,
        });

        // Check dialog state from localStorage (might have been opened by another tab)
        const dialogOpenTimestamp = readTimestamp(STORAGE_KEYS.DIALOG_OPEN, 0);
        if (dialogOpenTimestamp > 0 && !isDialogOpenRef.current) {
          checkShouldShowDialog();
        } else if (!isDialogOpenRef.current) {
          checkShouldShowDialog();
        }

        if (leader.isLeader() && !refreshTimeoutRef.current) {
          scheduleRefresh();
        } else if (!leader.isLeader()) {
          // Leader may have died while we were hidden/sleeping — attempt promotion
          leader.tryClaimLeadership().then((promoted) => {
            if (promoted) {
              sessionLog('[OIDCSession] 👑 Promoted to leader on visibility change');
              scheduleRefresh();
            }
          });
        }
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    const handleStorageEvent = (event) => {
      if (event.key === null) {
        sessionLog('[OIDCSession] 🗑️ Storage cleared by another tab - logging out');
        handleLogout('storage_cleared');
        return;
      }

      // Handle OIDC storage key removal (logout via removeItem)
      // IMPORTANT: Don't react immediately. The removal could be from:
      //   1. oidc-client-ts cache rotation during signinSilent() (removes old entry
      //      before writing new one)
      //   2. Our own handleLogout in another tab - BroadcastChannel handles this faster
      //   3. SDK clearing cache after a failed refresh token exchange
      // Debounce to let BroadcastChannel LOGOUT_REQUESTED arrive first (case 2),
      // and verify auth state before forcibly logging out.
      if (event.key?.includes('oidc.user:') && !event.newValue && event.oldValue) {
        if (storageKeyRemovedTimeoutRef.current) {
          clearTimeout(storageKeyRemovedTimeoutRef.current);
        }

        // Dynamic debounce: allow more time when a refresh is in progress or scheduled.
        const isRefreshing = isRefreshingRef.current;
        const hasScheduledRefresh = !!refreshTimeoutRef.current;
        const debounceMs =
          isRefreshing || hasScheduledRefresh
            ? STORAGE_KEY_DEBOUNCE_REFRESHING_MS
            : STORAGE_KEY_DEBOUNCE_MS;

        sessionLog('[OIDCSession] 🗑️ oidc.user: key removed', {
          key: event.key,
          debounceMs,
          reason: isRefreshing
            ? 'refresh-in-progress'
            : hasScheduledRefresh
              ? 'refresh-scheduled'
              : 'no-refresh-pending',
          isLeader: leader.isLeader(),
          tabId: getTabId(),
        });

        storageKeyRemovedTimeoutRef.current = setTimeout(() => {
          storageKeyRemovedTimeoutRef.current = null;

          // If already logged out via BroadcastChannel, skip
          if (!oidcAuthRef.current?.user) {
            sessionLog('[OIDCSession] oidc.user: removed - already logged out');
            return;
          }

          // Fast check: was the key re-added to localStorage directly?
          // This handles the SDK's remove→set rotation without an SDK call.
          if (event.key && localStorage.getItem(event.key)) {
            sessionLog(
              '[OIDCSession] oidc.user: key re-added to localStorage - ignoring'
            );
            return;
          }

          // If a refresh is STILL in progress, extend the wait once more.
          // The SDK may write the new key after our debounce if the network
          // round-trip was particularly slow.
          if (isRefreshingRef.current) {
            sessionLog(
              '[OIDCSession] oidc.user: removed but refresh still in progress - extending wait'
            );
            storageKeyRemovedTimeoutRef.current = setTimeout(() => {
              storageKeyRemovedTimeoutRef.current = null;
              if (!oidcAuthRef.current?.user) return;
              if (event.key && localStorage.getItem(event.key)) return;
              sessionLog(
                '[OIDCSession] oidc.user: removed after extended wait - logging out'
              );
              handleLogout('storage_key_removed');
            }, STORAGE_KEY_DEBOUNCE_REFRESHING_MS);
            return;
          }

          sessionLog(
            '[OIDCSession] oidc.user: removed and key not re-added - logging out'
          );
          handleLogout('storage_key_removed');
        }, debounceMs);
        return;
      }

      if (event.key?.includes('oidc.user:') && event.newValue) {
        sessionLog('[OIDCSession] 🔄 New token stored by another tab');
        // Another tab stored a new token - update our refresh timestamp
        setTimeout(() => {
          activity.updateRefreshTimestamp(Date.now());
          if (leader.isLeader() && !refreshTimeoutRef.current) {
            scheduleRefresh();
          }
        }, 100);
      }
    };
    window.addEventListener('storage', handleStorageEvent);

    return () => {
      unsubscribeSession();
      leader.cleanup();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('storage', handleStorageEvent);

      if (refreshTimeoutRef.current) clearTimeout(refreshTimeoutRef.current);
      if (dialogCheckIntervalRef.current) clearInterval(dialogCheckIntervalRef.current);
      if (storageKeyRemovedTimeoutRef.current)
        clearTimeout(storageKeyRemovedTimeoutRef.current);
      // NOTE: Do NOT clear countdownIntervalRef here. It is managed by
      // showDialog/dismissDialog/handleLogout. Clearing it on effect re-run
      // kills the dialog countdown while the dialog is still visible, causing
      // the frozen-dialog-with-negative-times bug.
    };
  }, [
    isLoading,
    hasUser,
    handleSessionMessage,
    scheduleRefresh,
    checkShouldShowDialog,
    handleLogout,
    leader,
    activity,
  ]);

  const formatCountdown = useCallback(() => {
    if (countdown <= 0) return '0:00';
    const minutes = Math.floor(countdown / 60);
    const seconds = countdown % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }, [countdown]);

  return {
    // Dialog state
    isDialogOpen,
    expirationTimestamp: dialogExpirationTimestamp,
    countdown,
    countdownFormatted: formatCountdown(),

    // Actions
    continueSession: handleContinue,
    logout: handleLogout,
    dismissDialog,

    // Internal state (for debugging)
    isLeader: leader.isLeader(),

    // Activity helpers
    signalActivity: (source) => activity.signalActivity(source),
    isUserActive: activity.isUserActive,
    resetActivityTimestamp: activity.resetTimestamps,
  };
};

// ============================================================================
// EXPORTED UTILITY FUNCTIONS (for backward compatibility)
// ============================================================================

/**
 * Check if timeout dialog is open
 */
export const isTimeoutDialogOpen = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEYS.DIALOG_OPEN);
    return stored !== null;
  } catch (error) {
    return false;
  }
};

/**
 * Check if user is active
 */
export const isUserActive = () => getActivityManager().isUserActive();

/**
 * Signal user activity from external source
 */
export const signalActivity = (source = 'external') =>
  getActivityManager().signalActivity(source);

/**
 * Get last activity timestamp
 */
export const getLastActivityTimestamp = () => getActivityManager().getLastActivity();

/**
 * Get last refresh timestamp
 */
export const getLastRefreshTimestamp = () => getActivityManager().getLastRefresh();

export default useOIDCSession;
